import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client/react'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { useLingui } from '@lingui/react/macro'

import Field from '@/components/ui/Field'
import { SelectSpace } from '@/features/space'
import { QrCodeContent } from '../hooks/useQrCode'

function useFetchAllSpaces() {
  const { data, fetchMore } = useQuery(AdminAPI.space.QUERY, {
    variables: {
      pageSize: 100,
      sort: {
        field: GraphQL.SpaceSortField.Identifier,
        direction: GraphQL.SortDirection.Asc
      }
    }
  })

  const items = useMemo(() => {
    return (
      data?.querySpaces.items.map(space =>
        getFragment(SharedAPI.space.SPACE_FRAGMENT, space)
      ) ?? []
    )
  }, [data])

  useEffect(() => {
    if (data?.querySpaces.metadata.totalEntries === items.length) return
    if (!data?.querySpaces.metadata.pageNumber) return

    const page = data.querySpaces.metadata.pageNumber + 1

    fetchMore({
      variables: {
        page
      },
      updateQuery(previousQueryResult, { fetchMoreResult }) {
        return {
          querySpaces: {
            items: [
              ...previousQueryResult.querySpaces.items,
              ...fetchMoreResult.querySpaces.items
            ],
            metadata: fetchMoreResult.querySpaces.metadata
          }
        }
      }
    })
  }, [data, items, fetchMore])

  return items
}

export function SpaceUrl({
  appUrl,
  onChange
}: {
  appUrl: string
  onChange: (urls: QrCodeContent[]) => void
}) {
  const { t } = useLingui()
  const spaces = useFetchAllSpaces()

  const [spaceId, setSpaceId] = useState<number | null>(null)

  const { data } = useQuery(AdminAPI.space.GET, {
    variables: { id: spaceId ?? -1 },
    skip: !spaceId
  })

  useEffect(() => {
    const space = getFragment(SharedAPI.space.SPACE_FRAGMENT, data?.getSpace)

    const identifiers = space?.identifier
      ? [space?.identifier]
      : spaces.map(space => space.identifier)

    const contents = identifiers.map(identifier => {
      const token = btoa(JSON.stringify({ spaceIdentifier: identifier }))

      return { url: `${appUrl}/connect/${token}`, text: identifier }
    })

    onChange(contents)
  }, [appUrl, spaceId, data, spaces, onChange])

  return (
    <div>
      <Field label={t`Choose Room`} size="small">
        <SelectSpace
          onChange={setSpaceId}
          placeholder={t`All rooms`}
          id={spaceId}
        />
      </Field>
    </div>
  )
}
